import { Button } from '@heidi/design-system/button';
import { Popover, PopoverAnchor, PopoverContent } from '@heidi/design-system/popover';
import { TypographyCaption, TypographyP2SemiBold } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { X } from 'lucide-react';
import { FormattedMessage, useIntl } from 'react-intl';
import { trialEndDiscreetCardMessages } from './trial-end-discreet-card.messages';
import { useTrialEndDiscreetCard } from './use-trial-end-discreet-card';

/**
 * Post–trial-end "discreet" corner card surfaced to **low-usage** admins
 * (solo Clinician trial or Practice admin) immediately after their 90-day
 * trial ends. Acts as a soft-touch preview of the big trial-ended modal —
 * dismissing it (×) finalises "no thanks", while "Learn more" hands off to
 * {@link ModalId.NINETY_DAY_TRIAL_END_MODAL}.
 *
 * Subscribes to the modal queue via
 * {@link ModalId.NINETY_DAY_TRIAL_END_DISCREET_POPUP} and renders a non-modal
 * `Popover` anchored to the bottom-left corner of the viewport — same shell
 * as the pre-trial-end {@link TrialEndReminderPopup}.
 *
 * Cohort gating (which users see this) lives in
 * `launchModalsConfigAtom.ts` — this component renders whenever the queue
 * surfaces its ID; it does not re-check usage or role.
 *
 * Figma: https://www.figma.com/design/MSqFiRMYvicVZlaiWjmFG3/Trials---upgrades?node-id=10978-33856
 */
export const TrialEndDiscreetCard = () => {
  const intl = useIntl();
  const {
    isOpen,
    wasClinicianPlan,
    planName,
    handleLearnMore,
    handleCloseClick,
    handleOpenChange,
  } = useTrialEndDiscreetCard();

  const titleMessage = wasClinicianPlan
    ? trialEndDiscreetCardMessages.title
    : trialEndDiscreetCardMessages.titlePractice;
  const descriptionMessage = wasClinicianPlan
    ? trialEndDiscreetCardMessages.description
    : trialEndDiscreetCardMessages.descriptionPractice;

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange} modal={false}>
      <PopoverAnchor
        aria-hidden
        className="pointer-events-none fixed bottom-4 start-4 z-[55] size-px"
      />
      <PopoverContent
        side="top"
        align="start"
        sideOffset={0}
        onInteractOutside={(event) => event.preventDefault()}
        onOpenAutoFocus={(event) => event.preventDefault()}
        aria-label={intl.formatMessage(trialEndDiscreetCardMessages.ariaLabel)}
        data-testid="trial-end-discreet-card"
        className={cn(
          'w-[277px] max-w-[277px] overflow-hidden rounded-xl border border-border bg-surface-container p-0 shadow-sheet',
          'data-[state=open]:animate-in data-[state=closed]:animate-out',
          'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95'
        )}
      >
        <div className="relative flex flex-col gap-2 py-3">
          <Button
            type="button"
            variant="ghost"
            size="xxs"
            iconOnly
            aria-label={intl.formatMessage(trialEndDiscreetCardMessages.close)}
            onClick={handleCloseClick}
            className="absolute end-3 top-3 shrink-0 text-text-secondary hover:bg-surface-hover hover:text-text-primary"
          >
            <X />
          </Button>
          <div className="flex flex-col gap-1">
            <div className="px-4 py-0.5">
              <TypographyP2SemiBold className="pe-6 text-text-primary">
                <FormattedMessage {...titleMessage} />
              </TypographyP2SemiBold>
            </div>
            <div className="px-4">
              <TypographyCaption className="leading-snug text-text-secondary">
                <FormattedMessage
                  {...descriptionMessage}
                  values={{
                    planName,
                    emphasis: (chunks) => <span className="underline">{chunks}</span>,
                  }}
                />
              </TypographyCaption>
            </div>
          </div>

          <div className="px-4 pb-1 pt-0">
            <Button
              type="button"
              variant="default"
              size="xs"
              className="w-full"
              onClick={handleLearnMore}
            >
              <FormattedMessage {...trialEndDiscreetCardMessages.learnMoreCta} />
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
